import { useCallback, useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { Avatar, Confirm, Panel, PrimaryButton, Title, ago, uxd } from "@/components/admin/kit";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const FILTERS = [["pending", "Pending"], ["live", "Live"], ["scheduled", "Scheduled"], ["done", "Done"], ["all", "All"]];
const REASONS = ["Not safe for work", "Misleading link", "Spam", "Low quality picture"];
const TONES = { pending: "bg-amber-100 text-amber-800", live: "bg-emerald-100 text-emerald-800", scheduled: "bg-indigo-100 text-indigo-800", rejected: "bg-rose-100 text-rose-800" };

// "approved" is live or scheduled depending on the clock.
const stateOf = (ad) => {
  if (ad.status !== "approved") return ad.status;
  const now = Date.now();
  return new Date(ad.start) > now ? "scheduled" : new Date(ad.end) > now ? "live" : "done";
};
const when = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

function AdCard({ ad, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const id = ad.id;
  const state = stateOf(ad);
  const act = async (path, body, done) => {
    setBusy(true);
    try {
      await api(`/admin/ads/${id}/${path}`, { method: "POST", body });
      toast.success(done);
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const picture = ad.image_url && `${ORIGIN}${ad.image_url}`;
  return (
    <Panel testId={`ad-${id}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar src={ad.a} alt={`@${ad.h}`} size={32} />
          <div className="min-w-0 leading-tight">
            <a href={`/?at=${ad.h}`} className="block truncate font-semibold hover:underline">@{ad.h}</a>
            <span className="text-xs text-slate-500">{ago(ad.created_at)}</span>
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${TONES[state] || "bg-slate-100 text-slate-600"}`} data-testid={`ad-${id}-state`}>{state}</span>
      </div>
      {!picture ? (
        <div className="mb-3 grid aspect-video place-items-center rounded-xl bg-slate-100 px-4 text-center text-sm text-slate-500" data-testid={`ad-${id}-nopicture`}>
          {ad.slot === "sign" ? "House sign: caption and link only" : "No picture"}
        </div>
      ) : (
        <a href={picture} target="_blank" rel="noopener noreferrer" className="mb-3 block aspect-video overflow-hidden rounded-xl bg-slate-900">
          <img src={picture} alt={ad.caption || ad.slot_name} className="h-full w-full object-contain" data-testid={`ad-${id}-picture`} />
        </a>
      )}
      <div className="mb-3 space-y-1 text-sm">
        <div className="font-semibold">{ad.slot_name} <span className="font-normal text-slate-500">· {ad.kind} · {ad.hours} {ad.hours === 1 ? "hour" : "hours"} · {uxd(ad.price)} paid</span></div>
        {ad.caption && <div data-testid={`ad-${id}-caption`}>{ad.caption}</div>}
        {ad.link && (
          <div className="flex items-start gap-1.5">
            <span className="min-w-0 break-all font-mono text-xs text-slate-600" data-testid={`ad-${id}-link`}>{ad.link}</span>
            {/^https?:\/\//i.test(ad.link) && (
              <a href={ad.link} target="_blank" rel="noopener noreferrer" title="Open in a new tab" className="shrink-0 text-slate-500 hover:text-ink" data-testid={`ad-${id}-open`}>
                <ExternalLink size={14} />
              </a>
            )}
          </div>
        )}
        {ad.start && <div className="text-xs text-slate-500">{when(ad.start)} to {when(ad.end)}</div>}
        {ad.reason && <div className="text-xs text-rose-700">Reason: {ad.reason}</div>}
      </div>
      {ad.status === "pending" && (
        <div className="flex gap-2">
          <PrimaryButton tone="brand" disabled={busy} onClick={() => act("approve", undefined, "Ad approved")} testId={`ad-${id}-approve`}>Approve</PrimaryButton>
          <PrimaryButton tone="danger" disabled={busy} onClick={() => setRejecting(!rejecting)} testId={`ad-${id}-reject`}>Reject</PrimaryButton>
        </div>
      )}
      {rejecting && ad.status === "pending" && (
        <div className="mt-3 space-y-2.5" data-testid={`ad-${id}-reject-form`}>
          <div className="flex flex-wrap gap-1.5">
            {REASONS.map((r) => (
              <PrimaryButton key={r} tone={reason === r ? "ink" : "soft"} className="px-2.5 py-1 text-xs" onClick={() => setReason(r)}>{r}</PrimaryButton>
            ))}
          </div>
          <Input value={reason} maxLength={140} placeholder="Reason, the buyer sees it" onChange={(e) => setReason(e.target.value)} data-testid={`ad-${id}-reason`} />
          <PrimaryButton tone="danger" disabled={busy || !reason.trim()} onClick={() => act("reject", { reason: reason.trim() }, "Ad rejected and refunded")} testId={`ad-${id}-reject-send`}>
            Reject and refund
          </PrimaryButton>
        </div>
      )}
      {(state === "live" || state === "scheduled") && (
        <Confirm title="End this ad?" action="End ad" disabled={busy} testId={`ad-${id}-end`}
          text={`It comes down now and @${ad.h} gets the unused time back.`} onConfirm={() => act("end", undefined, "Ad ended and refunded")}>End ad</Confirm>
      )}
    </Panel>
  );
}

export function AdsPanel({ onPending }) {
  const [status, setStatus] = useState("pending");
  const [ads, setAds] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api(`/admin/ads?status=${status}`).then((d) => {
    setAds(d.ads);
    setError("");
    onPending(d.pending);
  }).catch((e) => setError(e.message)), [status, onPending]);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  const pick = (id) => { setAds(null); setStatus(id); };
  return (
    <div data-testid="ads-panel">
      <Title hint="Players pay upfront. Nothing goes up until you approve it; a rejected ad is refunded in full, an ended one for the time not used.">Ads</Title>
      <div className="mb-5 flex flex-wrap gap-2" data-testid="ads-filter">
        {FILTERS.map(([id, label]) => (
          <PrimaryButton key={id} tone={status === id ? "ink" : "soft"} onClick={() => pick(id)} testId={`ads-filter-${id}`}>{label}</PrimaryButton>
        ))}
      </div>
      {error ? <div className="text-rose-700" data-testid="ads-error">Couldn't load the ads: {error}</div>
        : !ads ? <div className="text-slate-500">Loading ads…</div>
        : !ads.length ? <div className="text-slate-500" data-testid="ads-empty">No {status === "all" ? "" : `${status} `}ads.</div>
        : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {ads.map((ad) => <AdCard key={ad.id} ad={ad} onChanged={load} />)}
          </div>
        )}
    </div>
  );
}
